import React, { useState, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import EditableSection from 'components/v2/EditableSection';
import { useTranslation } from 'react-i18next';
import LabelValue from 'components/ui/tailwind/label-value';
import Checkbox from 'components/v2/forms/Checkbox';
import SessionStore from 'stores/SessionStore';
import { sortableContainer, sortableElement, arrayMove, } from 'react-sortable-hoc';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import Spinner from 'components/ui/Spinner';
import { showToast } from 'stores/ToastStore';
import { checkPermissonForEditShipperBillingModule } from '../utils';
import { useGetDocumentTypes } from 'utils/DocumentsAPIUtils';
import { LoaderCircle } from 'lucide-react';
const DrayageInvoiceDocumentPreferencesSummary = ({ fields = [], invoiceDocumentPreferenceDocTypesMap, }) => {
    const { t } = useTranslation();
    return (React.createElement("div", { className: "pb-4" }, !fields.length ? (React.createElement(Spinner, { showMessage: false })) : (fields.map((pref) => {
        var _a;
        return (React.createElement(LabelValue, { key: pref.slug, sequence: pref.sequence, value: t((_a = invoiceDocumentPreferenceDocTypesMap[pref.slug]) === null || _a === void 0 ? void 0 : _a.localizationSlug) }));
    }))));
};
const DrayageInvoiceDocumentPreferencesForm = ({ fields, saving, onChange, onSaveShipper, onChangePreferences, onSaveSuccess, onSaveFail, invoiceDocumentPreferenceDocTypesMap, invoiceDocumentPreferenceDocTypes, }) => {
    const { t } = useTranslation();
    const [invoiceDocumentPreferenceDocTypesWithDisplayLabelSorted, setInvoiceDocumentPreferenceDocTypesWithDisplayLabelSorted,] = useState([]);
    useEffect(() => {
        prepareInvoiceDocumentsToDisplay(invoiceDocumentPreferenceDocTypes);
    }, [invoiceDocumentPreferenceDocTypes]);
    const invoiceDocumentPreferences = fields || [];
    const SortableContainer = sortableContainer(({ children }) => {
        return React.createElement("tbody", null, children);
    });
    const SortableItem = sortableElement(({ children }) => (React.createElement("tr", { className: "cursor-pointer" }, children)));
    const isChecked = (slug) => {
        return !!invoiceDocumentPreferences.find((pref) => (pref === null || pref === void 0 ? void 0 : pref.slug) === slug);
    };
    const requiredInvoiceDocTypes = ['shipment_invoice'];
    const isDisabled = (type) => {
        if (!SessionStore.hasFinanceRole()) {
            return true;
        }
        return requiredInvoiceDocTypes.includes(type);
    };
    const handleSortEnd = ({ oldIndex, newIndex }) => {
        const updatedPreferences = arrayMove(invoiceDocumentPreferences, oldIndex, newIndex).map((pref, i) => {
            const { slug } = pref;
            const sequence = i + 1;
            return {
                slug,
                sequence,
            };
        });
        onChangePreferences(updatedPreferences);
    };
    const handleRemove = (slug) => {
        const updatedPreferences = invoiceDocumentPreferences
            .filter((pref) => pref.slug !== slug)
            .map((pref, i) => (Object.assign(Object.assign({}, pref), { sequence: i + 1 })));
        onChangePreferences(updatedPreferences);
    };
    const saveDisabled = () => {
        return !SessionStore.hasFinanceRole();
    };
    const saveForm = async () => {
        try {
            await onSaveShipper();
            showToast(t('SHIPPER_UPDATED'));
            onSaveSuccess(fields);
        }
        catch (e) {
            showToast(e.message, '', 'error');
            onSaveFail();
        }
    };
    useEffect(() => {
        if (saving) {
            saveForm();
        }
    }, [saving]);
    function prepareInvoiceDocumentsToDisplay(invoiceDocumentPreferenceDocTypes) {
        // first, get translated slug and set it as displayLabel
        const invoiceDocumentPreferenceDocTypesWithDisplayLabel = invoiceDocumentPreferenceDocTypes.map((doc) => (Object.assign(Object.assign({}, doc), { displayLabel: t(doc.localizationSlug, { count: doc.count }) })));
        // sort the documents for display by the translation label
        const sorted = invoiceDocumentPreferenceDocTypesWithDisplayLabel.sort(sortDocumentBySlug);
        setInvoiceDocumentPreferenceDocTypesWithDisplayLabelSorted(sorted);
    }
    function sortDocumentBySlug(a, b) {
        if (a.value === 'shipment_invoice')
            return -1; // invoice will always be first
        if (a.displayLabel < b.displayLabel) {
            return -1;
        }
        if (b.displayLabel > a.displayLabel) {
            return 1;
        }
        return 0;
    }
    return invoiceDocumentPreferences.length ? (React.createElement("div", null,
        React.createElement("h4", { className: "my-0 mb-1" }, t('DOCUMENT_TYPES')),
        React.createElement("p", { className: "text-sm mb-4" }, t('PLEASE_SELECT_SEQ_BELOW')),
        React.createElement("div", { className: "grid sm:grid-cols-2 gap-4" }, invoiceDocumentPreferenceDocTypesWithDisplayLabelSorted.map((docType) => (React.createElement(Checkbox, { key: docType.type, checked: isChecked(docType.type), disabled: isDisabled(docType.type), label: docType.displayLabel, onChange: onChange(docType.type), id: docType.type })))),
        React.createElement("hr", { className: "my-4" }),
        React.createElement("div", { className: "overflow-x-auto mt-4 overyflow-y-auto" },
            React.createElement("table", { className: "default-table" },
                React.createElement("thead", null,
                    React.createElement("tr", null,
                        React.createElement("th", null, t('DOCUMENT')),
                        React.createElement("th", null, t('ACTIONS')))),
                React.createElement(SortableContainer, { onSortEnd: handleSortEnd, distance: 10 }, invoiceDocumentPreferences.map((pref, i) => {
                    const { slug, sequence } = pref || {};
                    const key = `${slug}-${sequence}`;
                    const docType = invoiceDocumentPreferenceDocTypesMap[slug];
                    return (React.createElement(SortableItem, { key: key, index: i, disabled: saveDisabled() },
                        React.createElement("td", null,
                            React.createElement("div", { className: "flex flex-wrap items-center space-x-2" },
                                React.createElement("div", { className: "w-8 h-8 rounded-full bg-gray-100 text-gray-700 inline-flex items-center justify-center" },
                                    React.createElement("span", null, sequence)),
                                React.createElement("span", { className: "flex-1 text-sm text-gray-800" }, t(docType === null || docType === void 0 ? void 0 : docType.localizationSlug)))),
                        React.createElement("td", null, !isDisabled(slug) && (React.createElement(DeleteButton, { onClick: () => handleRemove(slug), size: "small" })))));
                })))))) : (React.createElement(Spinner, { showMessage: false }));
};
const DrayageInvoiceDocumentPreferences = ({ data, onUpdateShipper, className, }) => {
    const { t } = useTranslation();
    const [preferencesData, setPreferencesData] = useState(data);
    const [initialData, setInitialData] = useState(data);
    const { data: invoiceDocumentData, isLoading } = useGetDocumentTypes({
        listType: 'invoice',
        showListType: true,
    });
    const invoiceDocumentPreferenceDocTypes = useMemo(() => (invoiceDocumentData === null || invoiceDocumentData === void 0 ? void 0 : invoiceDocumentData.getAvailableDocumentTypes) || [], [invoiceDocumentData]);
    const invoiceDocumentPreferenceDocTypesMap = useMemo(() => invoiceDocumentPreferenceDocTypes.reduce((map, pref) => (Object.assign(Object.assign({}, map), { [pref.type]: pref })), {}), [invoiceDocumentPreferenceDocTypes]);
    const getInitData = () => {
        if (data) {
            setPreferencesData(data);
            setInitialData(data);
        }
    };
    useEffect(() => {
        getInitData();
    }, [data]);
    const onChange = (slug) => {
        return (checked) => {
            if (!checked) {
                setPreferencesData((prev) => {
                    const updated = prev
                        .filter((pref) => pref.slug !== slug)
                        .map((pref, i) => (Object.assign(Object.assign({}, pref), { sequence: i + 1 })));
                    return [...updated];
                });
            }
            else {
                setPreferencesData((prev) => {
                    const sequence = prev.length + 1;
                    return [...prev, { slug, sequence }];
                });
            }
        };
    };
    const handleSetPreferences = (updates = []) => {
        setPreferencesData([...updates]);
    };
    const handleSave = async () => {
        let isValid = false;
        if (initialData.length === preferencesData.length) {
            const diff = preferencesData.filter(({ slug: prefSlug }) => !initialData.some(({ slug: initSlug }) => initSlug === prefSlug));
            if (diff.length) {
                isValid = true;
            }
        }
        else if (initialData.length !== preferencesData.length) {
            isValid = true;
        }
        if (isValid) {
            await onUpdateShipper(preferencesData);
        }
    };
    const handleAfterSave = (onSaveSuccess) => {
        return (updatedData = {}) => {
            setInitialData([...updatedData]);
            onSaveSuccess();
        };
    };
    if (isLoading) {
        return React.createElement(LoaderCircle, { className: "w-4 h-4 animate-spin ml-2" });
    }
    return (React.createElement(EditableSection, { title: t('INVOICE_DOCUMENT_PREFERENCES'), className: classNames(className), canEdit: checkPermissonForEditShipperBillingModule() }, (editing, saving, onSaveSuccess, onSaveFail) => (React.createElement(React.Fragment, null,
        !editing && (React.createElement(DrayageInvoiceDocumentPreferencesSummary, { fields: initialData, invoiceDocumentPreferenceDocTypesMap: invoiceDocumentPreferenceDocTypesMap })),
        editing && (React.createElement(DrayageInvoiceDocumentPreferencesForm, { fields: preferencesData, onChange: onChange, onChangePreferences: handleSetPreferences, onSaveShipper: () => handleSave(), saving: saving, onSaveSuccess: handleAfterSave(onSaveSuccess), onSaveFail: onSaveFail, invoiceDocumentPreferenceDocTypesMap: invoiceDocumentPreferenceDocTypesMap, invoiceDocumentPreferenceDocTypes: invoiceDocumentPreferenceDocTypes }))))));
};
export default DrayageInvoiceDocumentPreferences;
